import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { FEEDBACK_KINDS, MAX_FEEDBACK_MESSAGE, type FeedbackKind } from '@tv-casino/shared';
import { buildLabel } from '../lib/build.js';
import { sendFeedback } from '../lib/feedbackApi.js';
import {
  FEEDBACK_COPY,
  emptyFeedbackForm,
  feedbackContext,
  feedbackFormView,
  type FeedbackFormState,
} from '@tv-casino/viewmodels/components/feedbackModel.js';
import styles from './FeedbackButton.module.css';

export interface FeedbackButtonProps {
  /** The venue and table the reporter was at, when they were at one. */
  casinoCode?: string | null;
  tableCode?: string | null;
  gameType?: string | null;
  /** The route, for a caller that knows it better than the address bar does. */
  path?: string;
  className?: string;
}

/**
 * "Something broke / Idea" — the one way a player reports anything, from the
 * phone they are already holding.
 *
 * It opens a panel rather than a link out to a tracker: a player mid-round
 * will type two sentences and go back to the table, and anything that costs
 * them the page costs us the report. The build, the page and the table ride
 * along automatically, because those are the three things a bug report is
 * always missing and the three a player should never have to know.
 */
export function FeedbackButton({
  casinoCode,
  tableCode,
  gameType,
  path,
  className,
}: FeedbackButtonProps) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FeedbackFormState>(() => emptyFeedbackForm());
  const boxRef = useRef<HTMLTextAreaElement>(null);

  const vm = useMemo(() => feedbackFormView(form), [form]);

  const close = useCallback(() => setOpen(false), []);

  // Escape closes it, like every other overlay a phone knows.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  // Straight into the box: the player already decided to report something.
  useEffect(() => {
    if (open && form.status === 'idle') boxRef.current?.focus();
  }, [open, form.status]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!vm.canSend) return;
    setForm((prev) => ({ ...prev, status: 'sending', error: null }));
    try {
      const receipt = await sendFeedback({
        kind: form.kind,
        message: form.message.trim(),
        ...(form.contact.trim() ? { contact: form.contact.trim() } : {}),
        context: feedbackContext({
          path: path ?? window.location.pathname,
          build: buildLabel(),
          casinoCode,
          tableCode,
          gameType,
          width: window.innerWidth,
          height: window.innerHeight,
        }),
      });
      setForm((prev) => ({ ...prev, status: 'sent', receipt }));
    } catch (err) {
      setForm((prev) => ({
        ...prev,
        status: 'error',
        error: err instanceof Error ? err.message : 'That did not send. Try again in a moment.',
      }));
    }
  };

  const openPanel = () => {
    setForm(emptyFeedbackForm(form.kind));
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        className={className ? `${styles.opener} ${className}` : styles.opener}
        onClick={openPanel}
        data-feedback-open=""
      >
        Report a bug or idea
      </button>

      {open && (
        <div className={styles.scrim} onClick={close} data-feedback-scrim="">
          <div
            className={styles.panel}
            role="dialog"
            aria-modal="true"
            aria-label="Send feedback"
            // The scrim closes on a tap; a tap inside the panel is not one.
            onClick={(e) => e.stopPropagation()}
          >
            <header className={styles.head}>
              <h2 className={styles.title}>Tell us</h2>
              <button type="button" className={styles.close} onClick={close} aria-label="Close">
                ×
              </button>
            </header>

            {vm.thanks ? (
              <div className={styles.done}>
                <p className={styles.thanks}>{vm.thanks}</p>
                {vm.issueUrl && (
                  <a
                    className={styles.issueLink}
                    href={vm.issueUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {vm.issueLabel} on GitHub
                  </a>
                )}
                <div className={styles.doneRow}>
                  <button
                    type="button"
                    className={styles.ghost}
                    onClick={() => setForm(emptyFeedbackForm(form.kind))}
                  >
                    Send another
                  </button>
                  <button type="button" className={styles.send} onClick={close}>
                    Back to the table
                  </button>
                </div>
              </div>
            ) : (
              <form className={styles.form} onSubmit={submit}>
                <div className={styles.kinds} role="group" aria-label="What kind of feedback">
                  {FEEDBACK_KINDS.map((kind: FeedbackKind) => (
                    <button
                      key={kind}
                      type="button"
                      className={kind === form.kind ? styles.kindOn : styles.kind}
                      aria-pressed={kind === form.kind}
                      onClick={() => setForm((prev) => ({ ...prev, kind }))}
                    >
                      {FEEDBACK_COPY[kind].label}
                    </button>
                  ))}
                </div>

                <label className={styles.label} htmlFor="feedback-message">
                  {vm.heading}
                </label>
                <textarea
                  id="feedback-message"
                  ref={boxRef}
                  className={styles.box}
                  value={form.message}
                  maxLength={MAX_FEEDBACK_MESSAGE}
                  rows={5}
                  placeholder={vm.placeholder}
                  onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                />
                {vm.showCount && (
                  <p className={styles.count}>{vm.remaining} characters left</p>
                )}

                <label className={styles.label} htmlFor="feedback-contact">
                  How to reach you back (optional)
                </label>
                <input
                  id="feedback-contact"
                  className={styles.contact}
                  value={form.contact}
                  placeholder="email, or a name we know you by"
                  onChange={(e) => setForm((prev) => ({ ...prev, contact: e.target.value }))}
                />

                <p className={styles.fine}>
                  Sent with the build you are on and the table you are at, so we can find it.
                </p>

                {vm.error && (
                  <p className={styles.error} role="alert">
                    {vm.error}
                  </p>
                )}

                <button type="submit" className={styles.send} disabled={!vm.canSend}>
                  {vm.sendLabel}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
